import { Card } from "@/ui-components/ui/card";
import { ExternalLink, Terminal, ArrowRight } from "lucide-react";
import { useSearchParams } from "../../hooks/useRouter";
import { useExtensionAuth } from "@/hooks/useExtensionAuth";
import { AppHeader } from "../header/AppHeader";
import {
  EXTENSION_CALLBACK_PATH,
  EXTENSION_REDIRECT_INFO,
} from "@/lib/constants";

const ExtensionLogin = () => {
  const searchParams = useSearchParams();
  const editorUrl = searchParams.get("editor") || "cursor";
  const editorVersion = searchParams.get("editorVer") || "";
  const { data, error } = useExtensionAuth(editorUrl || "", editorVersion);

  // SECURITY: only known, allowlisted editors are ever used as a deep-link
  // scheme. `editorUrl` is attacker-controllable (?editor=) and `extensionInfo.url`
  // below becomes `<scheme>://callback?token=<authToken>` which flows into
  // window.location.href. If we let an arbitrary editor name become the scheme,
  // a crafted ?editor= could exfiltrate the user's tokens to a malicious local
  // protocol handler, or use javascript:/data: for XSS. So an unknown editor
  // falls back to Cursor (a known entry) — NEVER to a scheme derived from input.
  // Support a new editor by adding it to EXTENSION_REDIRECT_INFO, not via ?editor=.
  const extensionInfo =
    EXTENSION_REDIRECT_INFO[
      editorUrl as keyof typeof EXTENSION_REDIRECT_INFO
    ] ?? EXTENSION_REDIRECT_INFO.cursor;

  let extensionUrl = window.location.origin;
  if (data?.refreshToken && data?.clerkToken) {
    try {
      // extensionInfo.url is always a hardcoded allowlisted scheme here.
      const url = new URL(`${extensionInfo.url}://${EXTENSION_CALLBACK_PATH}`);
      url.searchParams.set("token", encodeURI(data?.refreshToken || ""));
      // FIXME: Return the clerk token too, it's still needed for share.specstory.com, remove once we dismiss old share
      url.searchParams.set("clerkToken", encodeURI(data?.clerkToken || ""));
      extensionUrl = url.href;
    } catch {
      // Defensive: keep the origin fallback rather than crash.
    }
  }

  const handleOpenExtension = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (data?.refreshToken && data?.clerkToken) {
      window.location.href = extensionUrl;
    }
  };

  const errorUI = () => (
    <div className="relative w-full max-w-lg">
      <div>Error fetching token: {error}</div>
    </div>
  );

  const loadingUI = () => (
    <div className="relative w-full max-w-lg">
      <div>Loading...</div>
    </div>
  );

  const loginUI = () => (
    <div className="relative w-full max-w-lg">
      {/* Logo Connection */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-4 mb-4">
          <div
            className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-md ${extensionInfo.bgColor}`}
          >
            <img
              src={extensionInfo.logo}
              alt={extensionInfo.name}
              className={`w-10 h-10 filter ${extensionInfo.logoClasses}`}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-muted" />
            <div className="w-2 h-2 rounded-full bg-muted" />
            <div className="w-2 h-2 rounded-full bg-muted" />
          </div>
          <div className="w-14 h-14 rounded-xl bg-muted border border-border flex items-center justify-center">
            <img
              src="/assets/SpecStory-logo.png"
              alt="SpecStory"
              className="w-10 h-10"
            />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-foreground">
          Connect {extensionInfo.name} to SpecStory
        </h1>
      </div>

      {/* Main Card */}
      <Card className="p-6 bg-card border-border shadow-lg">
        <h2 className="font-semibold text-foreground mb-4 text-lg">
          Complete your {extensionInfo.name} setup
        </h2>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Click the button below to finish authentication in{" "}
            {extensionInfo.name}.
          </p>

          <a
            onClick={handleOpenExtension}
            href={extensionUrl}
            className="flex gap-2 items-center justify-center w-full bg-gray-900 hover:bg-gray-800 text-white rounded-lg py-2.5 text-sm font-medium transition-all duration-300"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Open {extensionInfo.name}
          </a>

          {/* What happens next */}
          <Card className="p-3 bg-muted border-border">
            <div className="flex items-start gap-2">
              <Terminal className="w-4 h-4 text-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">
                  What happens next:
                </p>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>
                    Click "Open {extensionInfo.name}" when your browser asks to
                    open {extensionInfo.name}
                  </li>
                  <li>
                    {extensionInfo.name} will open and show a dialog - click to
                    accept
                  </li>
                  <li>
                    Authentication completes and you're ready to sync projects
                  </li>
                </ol>
              </div>
            </div>
          </Card>
        </div>
      </Card>

      {/* Already authenticated link */}
      <div className="mt-4">
        <a
          href="/"
          className="flex items-center justify-center gap-1.5 w-full px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-black/5 rounded-lg transition-all duration-200 group"
        >
          Already authenticated? Go to dashboard
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </a>
      </div>
    </div>
  );

  const content = error ? errorUI() : data ? loginUI() : loadingUI();

  return (
    <div className="h-full flex flex-col">
      <AppHeader />
      <div className="flex-1 min-h-0">
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          {/* Background gradients - using dark theme for extension name */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -bottom-32 -left-32 w-[600px] h-[600px] bg-gradient-to-tr from-gray-900/20 via-gray-900/5 to-transparent blur-3xl" />
            <div className="absolute -bottom-32 -right-32 w-[600px] h-[600px] bg-gradient-to-tl from-gray-800/15 via-gray-800/5 to-transparent blur-3xl" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-t from-gray-900/10 via-transparent to-transparent blur-3xl" />
          </div>
          {content}
        </div>
      </div>
    </div>
  );
};

export default ExtensionLogin;
